import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";
import CandidateEmailLog from "@/components/CandidateEmailLog";
import { Mail, Send, X, Sparkles, Clock as ClockIcon, History } from "lucide-react";

/**
 * Phase 151 — Compose email to a candidate.
 *
 * Opens from the Candidate profile. Lets the recruiter pick a template (or
 * start blank), edit subject + body in a TipTap rich editor, and send. The
 * backend uses the tenant's From-name / From-email / Reply-To.
 */
function fill(template, vars) {
    if (!template) return "";
    return template.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : ""));
}

export default function ComposeEmailDialog({ open, onClose, candidate }) {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [picked, setPicked] = useState("");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const [companyName, setCompanyName] = useState("");
    const [latestJobTitle, setLatestJobTitle] = useState("");
    // Phase 152 — scheduled send + signature append
    const [sendAt, setSendAt] = useState("");
    const [includeSignature, setIncludeSignature] = useState(true);
    const [signature, setSignature] = useState("");
    const [showHistory, setShowHistory] = useState(false);
    const [historyToken, setHistoryToken] = useState(0);

    useEffect(() => {
        if (!open) return;
        setPicked("");
        setSubject("");
        setBody("");
        setSendAt("");
        setIncludeSignature(true);
        // Templates
        api.get("/company/email-templates").then((r) => {
            const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
            setTemplates(list.filter((t) => t.audience === "candidate" && t.enabled !== false));
        }).catch(() => {});
        // Company name — one tiny request, cached across opens by the browser
        // (/company/theme returns only {theme}; /company returns {name,...})
        api.get("/company").then((r) => {
            setCompanyName(r.data?.name || "");
        }).catch(() => {});
        // Phase 152 — personal signature (append preview to body on send)
        api.get("/me/signature").then((r) => {
            setSignature(r.data?.signature_html || "");
        }).catch(() => setSignature(""));
        // Latest application's job title for template interpolation
        // (GET /candidates/{id} embeds applications[] with job_title)
        if (candidate?.id) {
            api.get(`/candidates/${candidate.id}`).then((r) => {
                const rows = Array.isArray(r.data?.applications) ? r.data.applications : [];
                const first = rows[0];
                setLatestJobTitle(first?.job_title || first?.job?.title || "");
            }).catch(() => setLatestJobTitle(""));
        }
    }, [open, candidate?.id]);

    const vars = useMemo(() => {
        const frontend = (typeof window !== "undefined") ? window.location.origin : "";
        return {
            candidate_name: candidate?.name || "",
            candidate_first_name: (candidate?.name || "").split(" ")[0] || "",
            candidate_email: candidate?.email || "",
            job_title: latestJobTitle || candidate?.applied_job_title || "",
            company_name: companyName || candidate?.company_name || "",
            portal_link: `${frontend}/seeker/applications`,
            recruiter_name: user?.name || user?.email || "",
        };
    }, [candidate, companyName, latestJobTitle, user]);

    const pickTemplate = (key) => {
        setPicked(key);
        const tpl = templates.find((t) => t.key === key);
        if (!tpl) {
            setSubject("");
            setBody("");
            return;
        }
        setSubject(fill(tpl.subject || tpl.default_subject || "", vars));
        setBody(fill(tpl.body_html || tpl.default_body || "", vars));
    };

    const send = async () => {
        if (!subject.trim()) return toast.error("Subject is required");
        const plain = (body || "").replace(/<[^>]+>/g, "").trim();
        if (!plain) return toast.error("Body is empty");
        setSending(true);
        try {
            const payload = {
                template_key: picked || null,
                subject: subject.trim(),
                body_html: body,
                include_signature: includeSignature,
            };
            if (sendAt) {
                // datetime-local → ISO (treat as local, Resend accepts UTC)
                const dt = new Date(sendAt);
                if (!isFinite(dt.getTime())) return toast.error("Invalid send time");
                if (dt.getTime() - Date.now() < 120_000) return toast.error("Send time must be at least 2 minutes away");
                payload.send_at = dt.toISOString();
            }
            const { data } = await api.post(`/candidates/${candidate.id}/compose-email`, payload);
            toast.success(
                data?.status === "scheduled"
                    ? `Email scheduled for ${new Date(data.scheduled_at).toLocaleString()}`
                    : `Email queued for ${candidate.email}`
            );
            setHistoryToken((v) => v + 1);
            setShowHistory(true);
            onClose?.(true);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setSending(false); }
    };

    if (!candidate) return null;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="!p-0 !gap-0 w-screen sm:w-[96vw] max-w-none sm:max-w-3xl h-[100svh] sm:h-auto sm:max-h-[92vh] rounded-none sm:rounded-xl !inset-0 sm:!inset-auto !top-0 sm:!top-1/2 !left-0 sm:!left-1/2 !translate-x-0 sm:!translate-x-[-50%] !translate-y-0 sm:!translate-y-[-50%] [overflow:clip] !flex flex-col" data-testid="compose-email-dialog">
                <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-border bg-secondary/50 shrink-0">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 bg-ikb/10 text-ikb rounded-md flex items-center justify-center shrink-0">
                            <Mail className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                            <DialogTitle className="font-display font-bold text-sm sm:text-base truncate">
                                Email {candidate.name || candidate.email}
                            </DialogTitle>
                            <div className="text-[11px] text-muted-foreground font-mono truncate">
                                To: {candidate.email || "(no email)"}
                            </div>
                        </div>
                    </div>
                    <button type="button" onClick={() => onClose?.()} className="h-8 w-8 rounded-md hover:bg-secondary flex items-center justify-center" data-testid="compose-email-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Template
                        </Label>
                        <select
                            value={picked}
                            onChange={(e) => pickTemplate(e.target.value)}
                            className="mt-1.5 w-full h-10 px-3 border border-border rounded-md bg-white text-sm"
                            data-testid="compose-template-picker"
                        >
                            <option value="">Blank — write a custom message</option>
                            {templates.map((t) => (
                                <option key={t.key} value={t.key}>{t.name} · {t.category}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <Label className="text-[10px] uppercase tracking-widest font-bold">Subject</Label>
                        <Input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Interview invite · Senior Backend Engineer"
                            className="h-10 mt-1.5"
                            data-testid="compose-subject"
                        />
                    </div>

                    <div>
                        <Label className="text-[10px] uppercase tracking-widest font-bold">Body</Label>
                        <div className="mt-1.5" data-testid="compose-body-wrap">
                            <RichTextEditor
                                value={body}
                                onChange={setBody}
                                placeholder={`Hi ${candidate.name?.split(" ")[0] || "there"},\n\n`}
                                minHeight={260}
                                testId="compose-body"
                            />
                        </div>
                    </div>

                    {/* Phase 152 — signature preview + toggle */}
                    {signature && (
                        <label className="flex items-start gap-2 text-xs cursor-pointer select-none" data-testid="compose-sig-row">
                            <input
                                type="checkbox"
                                checked={includeSignature}
                                onChange={(e) => setIncludeSignature(e.target.checked)}
                                className="mt-0.5"
                                data-testid="compose-include-signature"
                            />
                            <span className="flex-1">
                                <span className="font-medium">Append my signature</span>
                                <span
                                    className="block mt-1 p-2 bg-secondary/60 border border-border rounded text-[11px] prose prose-sm max-w-none"
                                    data-testid="compose-sig-preview"
                                    dangerouslySetInnerHTML={{ __html: signature }}
                                />
                            </span>
                        </label>
                    )}

                    {/* Phase 152 — schedule send */}
                    <div data-testid="compose-schedule-row">
                        <Label className="text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1">
                            <ClockIcon className="w-3 h-3" /> Schedule send (optional)
                        </Label>
                        <div className="flex items-center gap-2 mt-1.5">
                            <Input
                                type="datetime-local"
                                value={sendAt}
                                onChange={(e) => setSendAt(e.target.value)}
                                className="h-9 flex-1"
                                data-testid="compose-send-at"
                            />
                            {sendAt && (
                                <Button variant="outline" size="sm" onClick={() => setSendAt("")} className="h-9" data-testid="compose-clear-schedule">
                                    Clear
                                </Button>
                            )}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                            Leave blank to send immediately. The platform cron dispatches scheduled emails hourly.
                        </div>
                    </div>

                    {/* Phase 153 — email history (delivered / opened / clicked badges) */}
                    <div data-testid="compose-history-section">
                        <button
                            type="button"
                            onClick={() => setShowHistory((v) => !v)}
                            className="text-[11px] font-mono uppercase tracking-widest text-ikb inline-flex items-center gap-1 hover:underline"
                            data-testid="compose-toggle-history"
                        >
                            <History className="w-3 h-3" /> {showHistory ? "Hide history" : "Show history"}
                        </button>
                        {showHistory && (
                            <div className="mt-2">
                                <CandidateEmailLog candidateId={candidate.id} refreshToken={historyToken} />
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-4 sm:px-5 py-3 border-t border-border bg-secondary/40 flex items-center gap-2 shrink-0">
                    <div className="text-[11px] text-muted-foreground flex-1">
                        Sent from your workspace From-address · Replies go to the thread
                    </div>
                    <Button variant="outline" onClick={() => onClose?.()} className="h-9" data-testid="compose-cancel">
                        Cancel
                    </Button>
                    <Button onClick={send} disabled={sending || !candidate.email} className="h-9 bg-ikb hover:bg-[#001f7a] text-white" data-testid="compose-send">
                        <Send className="w-3.5 h-3.5 mr-1.5" />
                        {sending ? "Sending…" : sendAt ? "Schedule" : "Send"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
